import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, Coins, UserPlus, Play } from "lucide-react";
import { NotificationsBell } from "../components/Notifications";
import DailyClashCard from "../components/DailyClashCard";
import OpenClashes from "../components/OpenClashes";
import DailyRewardPop from "../components/DailyRewardPop";
import PrizesModal from "../components/PrizesModal";
import WalletModal from "../components/WalletModal";
import InviteModal from "../components/InviteModal";
import HistoryModal from "../components/HistoryModal";
import AuthForm from "../components/AuthForm";
import { AccountButton } from "../components/AccountButton";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import PepSpinWheel from "../components/PepSpinWheel";
import { GAMES, GameBg, GameBadge } from "../components/WagerPicker";
import WinnersTicker from "../components/WinnersTicker";
import TopEarners from "../components/TopEarners";
import SiteFooter from "../components/SiteFooter";
import WelcomeBonus, { welcomeSeen } from "../components/WelcomeBonus";
import { auth, getStoredUser, dailyApi, freeGame, shootoutApi } from "../lib/api";
import { Toaster } from "../components/ui/toaster";

const SPIN_COST = 150;

const Home = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(getStoredUser());
  const [showSignup, setShowSignup] = useState(false);
  const [dailyReward, setDailyReward] = useState(0);
  const [showPrizes, setShowPrizes] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const isNew = !user || user.is_guest;
  useEffect(() => {
    if (loaded && (!user || (user.is_guest && !user.free_game_used)) && !welcomeSeen()) setShowWelcome(true);
  }, [loaded, user?.is_guest, user?.free_game_used]); // eslint-disable-line react-hooks/exhaustive-deps
  const GAME_PATH = { draft: "/pep", penalties: "/penalties" };
  // First tap on a game is what creates the guest account (page views alone don't).
  const ensureGuest = async () => {
    if (user) return user;
    const u = await auth.ensure();
    setUser(u);
    return u;
  };
  // Welcome pick goes straight into a £10 game against Pep (falls back to the game's stake pop-up on error).
  const startWelcome = async (g) => {
    try {
      const u = await ensureGuest();
      freeGame.start();
      const stake = (u?.coins ?? 0) >= 10 ? 10 : 0;
      navigate(g.id === "draft" ? `/pep?stake=${stake}` : `/p/${(await shootoutApi.pep(stake)).shootout.id}`);
    } catch {
      navigate(GAME_PATH[g.id]);
    }
  };
  const [afterAuth, setAfterAuth] = useState(null);
  const gate = (fn) => {
    if (!user) return ensureGuest().then(() => { freeGame.start(); fn(); }).catch(() => fn());
    if (user && (!user.is_guest || !user.free_game_used)) {
      if (user.is_guest) freeGame.start();
      return fn();
    }
    setAfterAuth(() => fn);
    setShowSignup(true);
  };

  useEffect(() => {
    // Capture a referral code from the URL so it rides along on sign-up.
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) localStorage.setItem("fc_ref", ref);
    } catch (e) { /* no-op */ }

    auth.ensure({ create: false }).then(async (u) => {
      setUser(u);
      setLoaded(true);
      if (u?.is_guest && u.free_game_used) freeGame.end();
      if (!u || u.is_guest) return;
      const c = await dailyApi.checkin();
      if (c.granted > 0) {
        setDailyReward(c.granted);
        setUser((x) => ({ ...x, coins: c.coins }));
      }
    }).catch(() => setLoaded(true));
  }, []);

  return (
    <div className="theme-light bg-app min-h-screen relative overflow-hidden flex flex-col">
      {/* top bar */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[#E2E8F0]">
        <div className="max-w-md lg:max-w-6xl w-full mx-auto px-4 lg:px-8 h-14 flex items-center justify-between gap-2">
        <div data-testid="header-logo" className="flex items-center gap-2 font-display font-extrabold uppercase text-lg tracking-wide shrink-0">
          <span className="w-7 h-7 rounded-md bg-[#10B981] flex items-center justify-center"><Swords className="w-4 h-4 text-[#0F172A]" strokeWidth={2.5} /></span>
          <span className="hidden min-[400px]:inline text-white">Footie Clash</span>
        </div>
        <div className="flex items-center gap-2">
          {user && !user.is_guest && (
            <button data-testid="home-friends-btn" onClick={() => navigate("/friends")} title="Friends"
              className="relative rounded-xl bg-white/5 border border-white/10 p-2 hover:border-[#10B981]/50 transition-colors shrink-0">
              <UserPlus className="w-4 h-4 text-slate-200" />
            </button>
          )}
          {user && !user.is_guest && <NotificationsBell />}
          {user && (
            <>
              <button
                data-testid="home-coin-balance"
                onClick={() => (user.is_guest ? setShowSignup(true) : setShowWallet(true))}
                title={user.is_guest ? "Sign up to keep your balance" : "Open wallet"}
                className="flex items-center gap-1.5 rounded-lg bg-[#1E293B] border border-[#334155] px-2.5 py-1.5 hover:border-[#475569] transition-colors"
              >
                <Coins className="w-4 h-4 text-[#F59E0B]" />
                <span className="font-display font-bold text-lg text-white tabular" data-testid="home-coins">
                  £{user.coins.toLocaleString()}
                </span>
                <span data-testid="virtual-tag" className="ml-0.5 rounded bg-[#334155] text-[#94A3B8] text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5">virtual</span>
              </button>
            </>
          )}
          <AccountButton
            user={user && !user.is_guest ? user : null}
            onSignIn={() => setShowSignup(true)}
            onLogout={() => { auth.logout(); localStorage.setItem("fc_dev_unpin", "1"); setUser(null); }}
            onHistory={() => setShowHistory(true)}
            onPrizes={() => setShowPrizes(true)}
            onInvite={() => setShowInvite(true)}
          />
        </div>
        </div>
      </header>


      <main className="relative z-10 flex-1 w-full max-w-md lg:max-w-6xl mx-auto px-4 lg:px-8 pt-7 pb-10">
        <WinnersTicker />
        <div className={`lg:grid lg:gap-x-10 lg:items-start ${isNew ? "lg:max-w-3xl lg:mx-auto" : "lg:grid-cols-[minmax(0,1fr)_420px]"}`}>
        <div>
        <section className="fade-up mt-6 lg:mt-10">
          <div className="text-[12px] label-eyebrow text-[#10B981]">Draft · Shoot · Win £</div>
          <h1 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mt-1">Footie Clash</h1>
          <p className="text-[#94A3B8] text-[15px] lg:text-lg mt-2 leading-relaxed lg:max-w-xl">
            Draft your XI, play a penalty shootout, wager a mate and take their £. Winner takes the pot.
          </p>
        </section>

        {user && !user.is_guest && <DailyClashCard userKey={user.email} onOpen={() => navigate("/daily")} />}

        {/* two games: pick one, choose a stake, then play Pep or wager a mate */}
        <div className="mt-6 w-full space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-5 fade-up">
          {GAMES.map((g) => (
            <button
              key={g.id}
              data-testid={`home-game-${g.id}`}
              onClick={() => gate(() => navigate(GAME_PATH[g.id]))}
              className="on-dark relative overflow-hidden w-full rounded-2xl px-5 py-6 lg:px-7 lg:py-8 text-left text-white border-2 border-[#34D399] shadow-[0_0_32px_rgba(16,185,129,0.35)] transition-transform duration-200 hover:scale-[1.015] active:scale-[0.98]"
            >
              <GameBg game={g} />
              <span className="relative flex items-center gap-3">
                <GameBadge game={g} size="w-14 h-14 lg:w-16 lg:h-16" icon="w-8 h-8 lg:w-9 lg:h-9" />
                <span className="font-display font-black uppercase text-4xl lg:text-5xl leading-[0.9] tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">{g.title}</span>
              </span>
              <span className="relative mt-5 flex items-end justify-between gap-3">
                <span data-testid={`home-game-${g.id}-gain`} className="leading-tight">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-[#A7F3D0]">Win up to</span>
                  <span className="block font-display font-black text-3xl tabular drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">£{Math.max(...g.stakes) * 2}</span>
                </span>
                <span data-testid={`home-game-${g.id}-play`} className="rounded-xl px-5 py-3 text-lg font-extrabold uppercase tracking-wide flex items-center gap-2 bg-[#10B981] text-[#0F172A] shadow-lg shrink-0">
                  <Play className="w-5 h-5 fill-current" /> Play
                </span>
              </span>
            </button>
          ))}
        </div>


        {!isNew && <OpenClashes refreshKey={user?.email} />}
        </div>

        {!isNew && (
        <aside className="lg:col-start-2 lg:row-start-1 lg:row-span-2">
        {/* spin the wheel (signed-in players only) */}
        <div className="mt-10 w-full fade-up">
          <PepSpinWheel
            inline
            repeatable
            cost={SPIN_COST}
            freeSpin={!!user.has_free_spin}
            coins={user.coins}
            onCoins={(c) => setUser((u) => ({ ...u, coins: c, has_free_spin: false }))}
            eyebrow="Prize wheel"
            title="Spin the wheel"
            subtitle=""
          />
        </div>

        </aside>
        )}
        <div className="lg:col-start-1 lg:row-start-2"><TopEarners /></div>
        </div>
      </main>

      <SiteFooter />
      <DailyRewardPop open={dailyReward > 0} amount={dailyReward} onClose={() => setDailyReward(0)} />
      {user && <PrizesModal open={showPrizes} onClose={() => setShowPrizes(false)} />}
      {user && <WalletModal open={showWallet} onClose={() => setShowWallet(false)} coins={user.coins} />}
      <Dialog open={showSignup && (!user || user.is_guest)} onOpenChange={(o) => { setShowSignup(o); if (!o) setAfterAuth(null); }}>
        <DialogContent aria-describedby={undefined} className="theme-light bg-transparent border-0 p-0 w-[calc(100%-2rem)] max-w-sm shadow-none">
          <DialogTitle className="sr-only">Sign up</DialogTitle>
          <AuthForm onAuthed={(u) => { setUser(u); setShowSignup(false); const next = afterAuth; setAfterAuth(null); if (next) setTimeout(next, 150); }} title={user?.free_game_used ? "Your free games are used up" : "Sign up to play"} subtitle={user?.free_game_used ? "Sign up free (or log in) to keep playing. Anything you won is kept." : "Create your free account and get £30 virtual to start. Already have one? Log in below."} />
        </DialogContent>
      </Dialog>
      <InviteModal open={showInvite} onClose={() => setShowInvite(false)} refCode={user?.ref_code} />
      {showWelcome && <WelcomeBonus onPick={startWelcome} />}
      <HistoryModal open={showHistory} onClose={() => setShowHistory(false)} />
      <Toaster />
    </div>
  );
};

export default Home;
